import { useContext, useEffect, useState } from "react";

import Head from "../../components/CreateOrEditDocument/Head/Head";
import styles from "./CreateType.module.css";

import StepOne from "./StepOne/StepOne";
import StepTwo from "./StepTwo/StepTwo";
import api from "../../api/api";

import StepThree from "./StepThree/StepThree";
import type { GetTemplateResultSuccessType } from "../../shared/types/files.types";
import type {
  ICreateTemplateCredentials,
  ICreateTemplateFieldsCredentials,
} from "../../shared/types/templates.types";
import { useNavigate } from "react-router-dom";

import { LoaderContext } from "../../shared/Contexts/Loader/LoaderContext";
import TestQueriesResultModal from "../../components/TestQueriesResultModal/TestQueriesResultModal";
import type { TestQueryResult } from "../../components/TestQueriesResultModal/TestQueriesResultModal";

type FirstStepValuesType = {
  name: string;
  description: string;
  departmentId: string;
  departmentName: string;
  file: File;
};

type SecondStepValuesType = {
  keywords: { value: string; id: string }[];
  text: string;
};

const CreateType = () => {
  const navigate = useNavigate();

  const { setIsLoading } = useContext(LoaderContext);
  const [step, setStep] = useState(1);

  // 1 step
  const [firstStepData, setFirstStepData] =
    useState<FirstStepValuesType | null>(null);

  const [extractedData, setExtractedData] =
    useState<GetTemplateResultSuccessType | null>(null);

  const [jobId, setJobId] = useState<string | null>(null);

  // Save s3Key from extractedData after extractData step
  const [s3Key, setS3Key] = useState<string | null>(null);

  const handleSubmitFirstStep = async (values: FirstStepValuesType) => {
    setFirstStepData(values);
    setIsLoading(true);

    try {
      const extractedData = await api.extractData(values.file);
      setJobId(extractedData.jobId);
      setS3Key(extractedData.s3Key); // Save s3Key from backend response

      const pollResult = async (): Promise<GetTemplateResultSuccessType> => {
        while (true) {
          const result = await api.getTemplateResult(extractedData.jobId);

          // Проверяем, является ли результат нужным типом
          if ("forms" in result && "blocks" in result && "text" in result) {
            return result;
          }

          // Подождать 2 секунды перед следующей попыткой
          await new Promise((resolve) => setTimeout(resolve, 2000));
        }
      };

      const finalResult = await pollResult();
      setExtractedData(finalResult);
      setStep(2);
    } catch (error) {
      console.error("Ошибка при получении результата шаблона:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // 1 step

  // 2 step
  const [secondStepData, setSecondStepData] =
    useState<SecondStepValuesType | null>(null);

  const handleSubmitSecondStep = (values: SecondStepValuesType) => {
    setSecondStepData(values);
    setStep(3);
  };

  // 2 step

  useEffect(() => {
    console.log("secondStepData: ", secondStepData);
  }, [secondStepData]);

  const submitCreationType = async (
    fields: ICreateTemplateFieldsCredentials[]
  ) => {
    if (!firstStepData) return;
    if (!secondStepData) return;
    if (!jobId) return;
    const dataToSend: ICreateTemplateCredentials = {
      jobId,
      name: firstStepData.name,
      description: firstStepData.description,
      departmentId: firstStepData.departmentId,
      documentTypeKeyword: secondStepData.keywords.map((keyword) => {
        return { keyword: keyword.value };
      }),
      fields,
    };
    const result = await api.createTemplate(dataToSend);
    navigate("/settings/documents-library");
    console.log(result);
  };

  // Test Queries Modal State
  const [testQueriesModalOpen, setTestQueriesModalOpen] = useState(false);
  const [testQueriesResults, setTestQueriesResults] = useState<
    TestQueryResult[]
  >([]);

  const handleTestQueries = async (
    queries: { field: string; question: string }[]
  ) => {
    if (!s3Key) {
      alert("s3Key is missing. Please upload and extract a file in Step 1.");
      return;
    }

    setIsLoading(true);

    try {
      const { jobId } = await api.startTestQueriesAsync({
        s3Key,
        queries,
      });

      const pollResult = async (): Promise<{
        status: string;
        result: { query: string; answer: string; confidence: number }[];
      }> => {
        while (true) {
          const result = await api.getTestQueriesResult(jobId);
          if (result.status === "SUCCEEDED") {
            return result;
          }
          await new Promise((resolve) => setTimeout(resolve, 2000));
        }
      };

      const finalResult = await pollResult();
      // Map backend result to include field name
      const resultsWithField: TestQueryResult[] = finalResult.result.map(
        (r) => {
          const field =
            queries.find((q) => q.question === r.query)?.field || "";
          return { ...r, field };
        }
      );
      setTestQueriesResults(resultsWithField);
      setTestQueriesModalOpen(true);
    } catch (error) {
      console.error("Error during test queries:", error);
      alert("An error occurred while testing queries. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <Head
        title="Crear Documento"
        description={
          step === 1
            ? "Preparación de la plantilla"
            : step === 2
            ? "Selección de propiedades"
            : "Configuración de propiedades"
        }
        step={step}
      />
      {step === 1 && (
        <StepOne savedData={firstStepData} onSubmit={handleSubmitFirstStep} />
      )}
      {step === 2 && extractedData && (
        <StepTwo
          // savedData={secondStepData}
          extractedData={extractedData}
          onSubmit={handleSubmitSecondStep}
          savedData={secondStepData}
          getBack={() => {
            setStep(1);
          }}
        />
      )}
      {step === 3 && extractedData && (
        <StepThree
          extractedData={extractedData}
          onSubmit={submitCreationType}
          onTestQueries={handleTestQueries}
          getBack={() => {
            setStep(2);
          }}
        />
      )}
      {testQueriesModalOpen && (
        <TestQueriesResultModal
          isOpen={testQueriesModalOpen}
          onClose={() => setTestQueriesModalOpen(false)}
          results={testQueriesResults}
        />
      )}
    </div>
  );
};

export default CreateType;
